import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';
import CPBanner1 from "../../assets/img/website-images/Campus-tour-pharmacy.jpg"
import CPBanner2 from "../../assets/img/website-images/Powerful-almunies-pharmacy.jpg"

/* ─── Variants ─────────────────────────────────────────────────── */
const fadeInLeft = {
    hidden: { opacity: 0, x: -75 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
    },
};

const fadeInRight = {
    hidden: { opacity: 0, x: 75 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
    },
};

const fadeUp = {
    hidden: { opacity: 0, y: 50, scale: 0.97 },
    visible: (delay = 0) => ({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
    }),
};

const textStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15, delayChildren: 0.25 } },
};

const textChild = {
    hidden: { opacity: 0, y: 22 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: 'easeOut' },
    },
};

/* ─── Reusable animated image ──────────────────────────────────── */
function AnimatedImage({ src, alt, inView, direction = 'left', className }) {
    const imgRef = useRef(null);

    useEffect(() => {
        if (inView && imgRef.current) {
            gsap.fromTo(
                imgRef.current,
                { scale: 0.9, opacity: 0 },
                { scale: 1, opacity: 1, duration: 1.0, ease: 'power3.out', delay: 0.2 }
            );
        }
    }, [inView]);

    return (
        <motion.div
            className={className}
            variants={direction === 'left' ? fadeInLeft : fadeInRight}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
        >
            <motion.img
                ref={imgRef}
                src={src}
                alt={alt}
                style={{ opacity: 0 }}
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.4 }}
            />
        </motion.div>
    );
}

/* ─── Reusable icon box ────────────────────────────────────────── */
function AnimatedIconBox({ icon, inView }) {
    const iconRef = useRef(null);

    useEffect(() => {
        if (inView && iconRef.current) {
            gsap.fromTo(
                iconRef.current,
                { rotation: -20, scale: 0.5, opacity: 0 },
                { rotation: 0, scale: 1, opacity: 1, duration: 0.7, ease: 'back.out(2)', delay: 0.4 }
            );
        }
    }, [inView]);

    return (
        <div className="dnumber">
            <div className="date-box">
                <img ref={iconRef} src={icon} alt="icon" style={{ opacity: 0 }} />
            </div>
        </div>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function Campus() {

    /* ── Section 1 refs ── */
    const { ref: s1LeftRef,  inView: s1LeftInView  } = useInView({ threshold: 0.2, triggerOnce: true });
    const { ref: s1RightRef, inView: s1RightInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* ── Section 2 refs ── */
    const { ref: s2LeftRef,  inView: s2LeftInView  } = useInView({ threshold: 0.2, triggerOnce: true });
    const { ref: s2RightRef, inView: s2RightInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: floating bg decorations */
    const bg02Ref = useRef(null);
    const bg08Ref = useRef(null);

    useEffect(() => {
        if (bg02Ref.current) {
            gsap.to(bg02Ref.current, {
                y: -20, duration: 4.2, ease: 'sine.inOut', repeat: -1, yoyo: true,
            });
        }
        if (bg08Ref.current) {
            gsap.to(bg08Ref.current, {
                y: 18, duration: 5, ease: 'sine.inOut', repeat: -1, yoyo: true, delay: 1.2,
            });
        }
    }, []);

    return (
        <>
            {/* ══════════════════════════════════════════
                Section 1 — Campus Tour
            ══════════════════════════════════════════ */}
            <section
                className="steps-area2 p-relative fix"
                style={{ backgroundColor: '#0C72BA' }}
            >
                <div className="animations-02" ref={bg02Ref}>
                    <img src="/assets/img/bg/an-img-10.png" alt="an-img-01" />
                </div>

                <div className="container">
                    <div className="row align-items-center">

                        {/* Left: Text box */}
                        <div className="col-lg-6 col-md-12" ref={s1LeftRef}>
                            <motion.div
                                className="step-box step-box2"
                                variants={fadeUp}
                                custom={0}
                                initial="hidden"
                                animate={s1LeftInView ? 'visible' : 'hidden'}
                            >
                                <AnimatedIconBox
                                    icon="/assets/img/icon/fea-icon01.png"
                                    inView={s1LeftInView}
                                />

                                <motion.div
                                    className="text"
                                    variants={textStagger}
                                    initial="hidden"
                                    animate={s1LeftInView ? 'visible' : 'hidden'}
                                >
                                    <motion.h2 variants={textChild}>Campus Tour</motion.h2>
                                    <motion.p variants={textChild}>
                                        Experience the Akash Institute of Pharmacy campus firsthand.
                                        From our state-of-the-art Pharmaceutics, Pharmacology, and
                                        Pharmaceutical Chemistry laboratories to our spacious classrooms
                                        and digital library — our campus is designed to inspire learning
                                        and discovery. Visit us to meet our faculty, explore our facilities,
                                        and get a feel for student life at AIP before you make your decision.
                                    </motion.p>
                                </motion.div>
                            </motion.div>
                        </div>

                        {/* Right: Image */}
                        <div className="col-lg-6 col-md-12" ref={s1RightRef}>
                            <AnimatedImage
                                src={CPBanner1}
                                alt="class image"
                                inView={s1RightInView}
                                direction="right"
                                className="step-img2"
                            />
                        </div>

                    </div>
                </div>
            </section>

            {/* ══════════════════════════════════════════
                Section 2 — Powerful Alumni
            ══════════════════════════════════════════ */}
            {/* <section
                className="steps-area2 p-relative fix"
                style={{ backgroundColor: '#0C72BA' }}
            >
                <div className="container">
                    <div className="animations-08" ref={bg08Ref}>
                        <img src="/assets/img/bg/an-img-20.png" alt="contact-bg-an-01" />
                    </div>

                    <div className="row align-items-center">

                        
                        <div className="col-lg-6 col-md-12" ref={s2LeftRef}>
                            <AnimatedImage
                                src={CPBanner2}
                                alt="class image"
                                inView={s2LeftInView}
                                direction="left"
                                className="step-img3"
                            />
                        </div>
                        
                      
                        <div className="col-lg-6 col-md-12" ref={s2RightRef}>
                            <motion.div
                                className="step-box step-box3"
                                variants={fadeUp}
                                custom={0}
                                initial="hidden"
                                animate={s2RightInView ? 'visible' : 'hidden'}
                            >
                                <AnimatedIconBox
                                    icon="/assets/img/icon/fea-icon03.png"
                                    inView={s2RightInView}
                                />

                                <motion.div
                                    className="text"
                                    variants={textStagger}
                                    initial="hidden"
                                    animate={s2RightInView ? 'visible' : 'hidden'}
                                >
                                    <motion.h2 variants={textChild}>Powerful Alumni</motion.h2>
                                    <motion.p variants={textChild}>
                                        Our alumni are making a meaningful impact across the pharmaceutical
                                        and healthcare industry — from Quality Assurance and Regulatory
                                        Affairs to Clinical Research and Drug Development. At AIP, we take
                                        pride in the success of every graduate. Our alumni network serves
                                        as a bridge between current students and industry professionals,
                                        opening doors to mentorship, internships, and career opportunities
                                        across the pharma sector.
                                    </motion.p>
                                </motion.div>
                            </motion.div>
                        </div>

                    </div>
                </div>
            </section> */}
        </>
    );
}

export default Campus;
